import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Moon, Sun } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_fundingbridge/artifacts/6f4haw8y_9161E8E4-2A18-454E-B39C-D1A5E84AD155.png';

const Terms = () => {
  const { isDark, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-colors duration-300">
      {/* Header */}
      <header className="glass-effect border-b border-slate-200 dark:border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <img src={LOGO_URL} alt="Our Cap Logo" className="h-12 w-auto object-contain" />
            </Link>
            <Button onClick={toggleTheme} variant="outline" size="sm" className="p-2">
              {isDark ? <Sun className="w-4 h-4 text-yellow-500" /> : <Moon className="w-4 h-4 text-slate-700" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-slate-900 dark:text-slate-100 mb-8">Terms & Conditions</h1>
        
        <div className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-slate-700 dark:text-slate-300">
          <p className="text-lg">
            <strong>Effective Date:</strong> January 1, 2026
          </p>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">1. Agreement to Terms</h2>
            <p>
              By accessing or using Our Cap ("Platform"), you agree to be bound by these Terms and Conditions ("Terms"). 
              If you do not agree to these Terms, please do not use our Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">2. Description of Services</h2>
            <p>
              Our Cap is a coordination platform that connects Borrowers with Liquidity Support Partners (LSPs) for 
              real estate transactions requiring proof of reserves. We facilitate the coordination process but are 
              <strong> not a lender</strong> and do not provide loan funds.
            </p>
            <p className="mt-4">Our services include:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Deal submission and management for Borrowers</li>
              <li>Liquidity verification for LSPs</li>
              <li>Coordination between parties through our admin team</li>
              <li>Document management and e-signature facilitation</li>
              <li>Payment processing for platform fees</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">3. User Accounts</h2>
            <p>To use our Platform, you must:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Be at least 18 years old</li>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">4. Fees and Payments</h2>
            <p>Our fee structure includes:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>Upfront Intake Fee:</strong> $750 (non-refundable, due upon deal submission)</li>
              <li><strong>Liquidity Coordination Fee:</strong> 5% of the liquidity amount provided</li>
              <li><strong>Processing Fee:</strong> $549</li>
              <li><strong>Wire Fee:</strong> $100</li>
            </ul>
            <p className="mt-4">
              All closing fees are due at the time of transaction closing. Failure to pay fees may result in 
              termination of services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">5. No Guarantee of Results</h2>
            <p>
              Our Cap does not guarantee loan approval, deal closing, or any specific outcome. We provide a 
              coordination platform only. Users are responsible for their own due diligence and decision-making.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">6. Privacy and Confidentiality</h2>
            <p>
              We maintain strict privacy controls between Borrowers and LSPs. Neither party will have direct 
              access to the other's identifying information unless approved by our admin team and both parties consent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">7. Prohibited Conduct</h2>
            <p>Users may not:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Provide false or misleading information</li>
              <li>Attempt to contact other parties outside the Platform to circumvent fees</li>
              <li>Use the Platform for any illegal purpose</li>
              <li>Interfere with Platform security or functionality</li>
              <li>Share account credentials with others</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">8. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Our Cap shall not be liable for any indirect, incidental, 
              special, consequential, or punitive damages arising from your use of the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">9. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Continued use of the Platform after changes 
              constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">10. Contact Information</h2>
            <p>
              For questions about these Terms, please contact us at:
            </p>
            <p className="mt-2">
              <strong>Email:</strong> legal@ourcap.com<br />
              <strong>Address:</strong> Our Cap LLC, Business Address
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <img src={LOGO_URL} alt="Our Cap Logo" className="h-12 w-auto mx-auto mb-4 object-contain" />
            <p className="text-sm text-slate-600 dark:text-slate-400">&copy; {new Date().getFullYear()} Our Cap. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
